import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { BUILD_BASE, BUILD_BRANCH, BUILD_COMMIT, IS_OFF_MAIN } from '@/buildInfo';
import { Badge } from '@/components/ui';
import { cx } from '@/lib/cx';

/*
 * ⚠️ ABSOLUTE, NEVER `./environments.json`.
 *
 * The apex and every preview read the SAME manifest, and it is written at the
 * apex origin. Relative, a preview served from `/b/<slug>/` would resolve this
 * to `/b/<slug>/environments.json`, get a 404, fall into the swallow-the-error
 * path below, and render a switcher listing only itself — a feature that looks
 * like it is working and offers nothing. That is the failure nobody reports,
 * because a list of one is indistinguishable from "no other branches are
 * deployed yet".
 */
const MANIFEST_URL = '/environments.json';

/** One deployed artefact, as the deploy script writes it into the manifest. */
export interface EnvEntry {
  branch: string;
  /** `null` for the apex build; the preview path segment otherwise. */
  slug: string | null;
  /** `"/"` or `"/b/<slug>/"`. Compared against `BUILD_BASE` — see `isCurrent`. */
  url: string;
  sha?: string;
  isMain: boolean;
  /** `"ok"` | `"failed"`; anything else reads as ok, so a new value cannot hide a build. */
  status?: string;
}

/*
 * The build you are looking at, described from the constants baked into it.
 *
 * It exists so the switcher works with no network at all: the manifest is a
 * convenience, and the one environment we can always name is this one.
 */
const SELF: EnvEntry = {
  branch: BUILD_BRANCH,
  slug: null,
  url: BUILD_BASE,
  sha: BUILD_COMMIT,
  isMain: !IS_OFF_MAIN,
  status: 'ok',
};

/*
 * ⚠️ IDENTITY IS AN EXACT STRING COMPARE, DELIBERATELY UNNORMALISED.
 *
 * No trim, no lowercase, no trailing-slash repair. The manifest producer is
 * responsible for writing `url` exactly as Vite spells `base` — `"/"` or
 * `"/b/<slug>/"`, trailing slash included. A mismatch must surface as an entry
 * that is simply not marked current, which somebody can see, rather than be
 * papered over here: repairing it would hide a producer bug from the one screen
 * capable of revealing it, and leave "current" a guess.
 */
function isCurrent(env: EnvEntry): boolean {
  return env.url === BUILD_BASE;
}

/*
 * The manifest is a static file a deploy script writes, so it is untrusted in
 * the only sense that matters here: it may be half-written, an older shape, or
 * an HTML 404 body that happened to parse. Validate before mapping over it — a
 * non-array `environments` would throw inside render, and on the apex build
 * that is production.
 */
function readEnvironments(body: unknown): EnvEntry[] | null {
  if (typeof body !== 'object' || body === null) return null;
  const raw = (body as { environments?: unknown }).environments;
  if (!Array.isArray(raw)) return null;

  const entries: EnvEntry[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const row = item as Record<string, unknown>;
    // `branch` and `url` are the two fields nothing downstream can do without:
    // one is the label, the other is both the identity and the destination.
    if (typeof row.branch !== 'string' || typeof row.url !== 'string') continue;
    entries.push({
      branch: row.branch,
      slug: typeof row.slug === 'string' ? row.slug : null,
      url: row.url,
      sha: typeof row.sha === 'string' ? row.sha : undefined,
      isMain: row.isMain === true,
      status: typeof row.status === 'string' ? row.status : undefined,
    });
  }
  return entries;
}

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

/** The roving set inside the menu: every row, links and the current-build button alike. */
const MENU_ITEMS = '[role="menuitem"]';

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      {...iconProps}
      className={cx(
        'h-3.5 w-3.5 shrink-0 transition-transform duration-150 motion-reduce:transition-none',
        open && 'rotate-180',
      )}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * Which build am I looking at, and how do I get to another one.
 *
 * A pill for the top bar naming the running branch and short sha, opening a
 * menu of every environment the deploy manifest knows about. Amber whenever the
 * code is not `main`, so "I am on somebody's branch" is answerable at a glance
 * instead of by reading the URL.
 *
 * ── WHY THIS IS NOT BUILT OUT OF `Button` ──────────────────────────────────
 *
 * `Button` has four variants and none of them is a warning tone, and `cx` is a
 * plain join with no conflict resolution — so passing `bg-warning-soft`
 * alongside a variant's own `bg-surface` leaves which one wins to Tailwind's
 * emission order rather than to the order written here. That is a coin flip
 * that happens to render correctly on the day it is written. The trigger and
 * the rows are therefore spelled out, following the ghost variant's vocabulary
 * rather than fighting it. `Badge` IS reused, because a chip is exactly what it
 * is.
 */
export function BuildSwitcher() {
  const [open, setOpen] = useState(false);
  /*
   * `null` means "no usable manifest". The initial state and the failure state
   * are deliberately the same value, so no branch below has to tell apart "not
   * asked yet" from "asked and got nothing" — the answer is `[SELF]` either way.
   */
  const [fetched, setFetched] = useState<EnvEntry[] | null>(null);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  /*
   * Keyboard behaviour copied from `UserMenu` in `AppShell.tsx`, the app's
   * reference menu: focus moves to the first item on open, arrows rove, Escape
   * closes and hands focus back to the trigger, and Tabbing out closes it. Before
   * this, focus stayed on the trigger, Tab walked out with the menu still open,
   * and Escape dropped focus onto `body`.
   */
  const dismiss = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEMS)?.focus();
  }, [open]);

  function onRootBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []);
    if (items.length === 0) return;
    event.preventDefault();

    const current = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : event.key === 'ArrowDown'
            ? (current + 1) % items.length
            : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  }

  useEffect(() => {
    /*
     * ⚠️ Not decoration. StrictMode double-mounts in dev, and a slow manifest
     * can resolve after this component has gone — setting state then is a
     * console warning in front of whoever is debugging something unrelated.
     */
    let mounted = true;

    /*
     * `no-store` because the manifest changes on every deploy and being current
     * is its entire job. A cached copy keeps offering a branch deleted an hour
     * ago, and the click that follows is a 404.
     */
    fetch(MANIFEST_URL, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((body: unknown) => {
        if (mounted) setFetched(readEnvironments(body));
      })
      /*
       * ⚠️ SWALLOWED, AND THAT IS THE DESIGN. A missing file, a 404, invalid
       * JSON, an offline browser — every one of them must leave a working pill
       * listing this build alone. This is a developer convenience; putting an
       * error on screen because a static file was absent would be a fault
       * reported by the people it was never built for.
       */
      .catch(() => {
        if (mounted) setFetched(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const environments = useMemo(() => {
    const base = fetched && fetched.length > 0 ? fetched : [SELF];
    // The build on screen is always in the list, whatever the manifest says.
    const list = base.some(isCurrent) ? base : [SELF, ...base];
    /*
     * Copied before sorting: on the no-prepend path `list` IS the state array,
     * and `sort` mutates in place — reordering React's own value without ever
     * telling it anything changed.
     */
    return [...list].sort((a, b) =>
      a.isMain === b.isMain ? a.branch.localeCompare(b.branch) : a.isMain ? -1 : 1,
    );
  }, [fetched]);

  useEffect(() => {
    // Bound to the open state, so nothing listens on `document` through the
    // 99% of a session in which this menu is shut.
    if (!open) return;

    function onMouseDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }

    // `mousedown` per this component's spec. `UserMenu` next door listens on
    // `pointerdown`; either dismisses before the click lands, which is the
    // property that actually matters. Escape is handled on the trigger and the
    // menu (above), so focus can be returned rather than dropped on `body`.
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  // `BUILD_COMMIT` is `""` when CI did not supply one, and `@ ` with nothing
  // after it reads as a missing value rather than as an absent fact.
  const label = BUILD_COMMIT
    ? `Build: ${BUILD_BRANCH} @ ${BUILD_COMMIT} — switch environment`
    : `Build: ${BUILD_BRANCH} — switch environment`;

  return (
    <div ref={rootRef} className="relative" onBlur={onRootBlur}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onTriggerKeyDown}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={cx(
          'inline-flex h-8 max-w-full items-center gap-1.5 rounded-md border px-2 text-xs font-medium max-sm:h-10',
          'focus-visible:outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2',
          /*
           * Amber is `warning` in this codebase — there is no raw Tailwind
           * palette anywhere in `src/`, and `--warning` is the theme-aware amber
           * that reads correctly in both themes with no `dark:` variant.
           */
          IS_OFF_MAIN
            ? 'border-warning-border bg-warning-soft text-warning hover:border-warning'
            : 'border-border bg-surface text-fg-secondary hover:bg-surface-hover hover:text-fg',
        )}
      >
        {/*
         * Colour is never the only signal — the branch name sits beside it. The
         * dot follows the PILL's question ("is this off main"), which is why it
         * is amber-or-neutral here and emerald-for-main in the menu below,
         * where the question is "which of these is main".
         */}
        <span
          aria-hidden="true"
          className={cx(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            IS_OFF_MAIN ? 'bg-warning' : 'bg-fg-subtle',
          )}
        />
        {/*
         * Compact below `sm`: the branch name goes visually hidden (it stays the
         * accessible name, and the `title` still carries it), so the top bar
         * fits 320px. The dot keeps the amber/neutral signal on screen.
         */}
        <span className="max-w-[140px] truncate max-sm:sr-only">{BUILD_BRANCH}</span>
        {BUILD_COMMIT && (
          <span className="tabular hidden font-mono text-2xs opacity-70 sm:inline">
            {BUILD_COMMIT}
          </span>
        )}
        <ChevronIcon open={open} />
      </button>

      {/*
       * ⚠️ `z-30`, NOT `z-50`. The number is LOCAL: this pill is mounted inside
       * the top bar, which is `sticky` with a `z-index` and therefore its own
       * stacking context — so this orders the menu against its siblings in the
       * bar and says nothing about whether it clears the page. `z-50` would not
       * make it clear anything either; it would only park a build menu on the
       * ladder's drawer/Modal/toast rung (see `src/index.css`), which exists so
       * a dialog can cover the chrome. If this menu is ever painted over by
       * page content, the rung to move is the BAR's. `UserMenu` in
       * `AppShell.tsx` is the precedent and carries the same note.
       */}
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          onKeyDown={onMenuKeyDown}
          role="menu"
          aria-label="Environments"
          className="absolute top-full right-0 z-30 mt-1 w-72 overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
        >
          <div className="p-1">
            {environments.map((env) => {
              const current = isCurrent(env);
              const failed = env.status === 'failed';

              const body = (
                <>
                  <span
                    aria-hidden="true"
                    className={cx(
                      'h-1.5 w-1.5 shrink-0 rounded-full',
                      failed ? 'bg-danger' : env.isMain ? 'bg-success' : 'bg-warning',
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate text-left">{env.branch}</span>
                  {env.sha && (
                    <span className="tabular shrink-0 font-mono text-2xs text-fg-subtle">
                      {env.sha}
                    </span>
                  )}
                  {current && <Badge tone="neutral">current</Badge>}
                  {/* Not doubled up on the current row: two chips on one line
                      read as two states rather than one row carrying two facts. */}
                  {failed && !current && <Badge tone="danger">failed</Badge>}
                </>
              );

              const rowClass = cx(
                'flex h-8 w-full items-center gap-2 rounded-md px-2 text-xs max-sm:h-10',
                'focus-visible:outline-focus-ring focus-visible:outline-2 focus-visible:-outline-offset-2',
                current
                  ? 'bg-surface-hover font-medium text-fg'
                  : 'text-fg-secondary hover:bg-surface-hover hover:text-fg',
                // Dimmed, never hidden and never disabled — opening a failed
                // build to see what broke is a reason somebody came here.
                failed && !current && 'opacity-60',
              );

              /*
               * ⚠️ THE CURRENT ROW CARRIES NO `href`. Every other row is a real
               * navigation (below), so a link to where you already are would be
               * a full page reload dressed up as a choice. A control that goes
               * nowhere is the only honest rendering of "you are here".
               */
              if (current) {
                return (
                  <button
                    key={env.url}
                    type="button"
                    role="menuitem"
                    aria-current="true"
                    title={failed ? 'Last build failed' : undefined}
                    className={rowClass}
                  >
                    {body}
                  </button>
                );
              }

              /*
               * A plain anchor, and a full page load deliberately: each
               * environment is a DIFFERENT built artefact under a different base
               * path. A router push would leave THIS bundle running and merely
               * change the URL, so the pill would name a branch it is not
               * executing — the exact confusion this component exists to end.
               */
              return (
                <a
                  key={env.url}
                  href={env.url}
                  role="menuitem"
                  title={failed ? 'Last build failed' : undefined}
                  // Closed before the browser leaves, so a slow or cancelled
                  // navigation does not leave the menu hanging open.
                  onClick={() => setOpen(false)}
                  className={rowClass}
                >
                  {body}
                </a>
              );
            })}
          </div>

          <p className="border-t border-border px-3 py-2 text-2xs text-fg-subtle">
            Push a branch — its preview appears here.
          </p>
        </div>
      )}
    </div>
  );
}
